/**
 * DimensionQuickSearch — product-type toggle + season toggle + direct
 * dimension entry. Standalone white card at the top of the filter list.
 *
 * Replaces the older dropdown controls:
 *   1. the "Produkt" dropdown filter   → the 3-step toggle (Däck/Fälg/Hjul)
 *   2. the global season tab strip       → the season toggle (under it)
 *   3. the Däckbredd/Profil/Diameter     → the typed dimension row(s)
 *      dropdown filters
 *   4. the Skick accordion filter        → the Alla/Nya/Begagnade toggle
 *   5. the Fälgtyp accordion filter      → a toggle (rims + wheels)
 *   6. the Pris accordion filter         → a range slider (like ET/navhål)
 *
 * Group order is the same on a computer and in the phone's sheet
 * (P25009-491 BR-5): Säsong, Däcktyp, Skick, Däckdimension, Bultcirkel, ET,
 * Navhål, Fälgtyp, Pris — Län follows as the page's own filter. There is
 * no product toggle and no Bilmärke/Bilmodell field on the search page any
 * more: both are chosen in the car bar.
 *
 * No search button: editing a field filters live (debounced), exactly
 * like the old dropdown filters. The fields are driven by the URL, so
 * they stay in sync with the REG lookup, the car's recommended-size
 * chips, the result pills, and the "Ny sökning" reset.
 *
 * Writes the exact query params the SearchPage already filters on
 * (pub_adType, pub_tireWidthFront/Rear, pub_profileFront/Rear,
 * pub_diameter, pub_rimWidthFront/Rear, pub_tiretype). Staggered = an OR
 * of two exact sizes sharing the diameter, never a width×profile
 * cross-product.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';

import { RangeSlider } from '../../../components';
import { boltCircleSpellings, boltCircleText } from '../../../util/boltCircleCore';
import { DISTANCE_CHIPS, radiusLabel, useDistanceFilter } from '../distanceFilter';
import css from './DimensionQuickSearch.module.css';

/* Plain-text chips, no icons (P25009-486 BR-2). The product type is also
 * picked in the car's tabs, so with a car this row is left out. */
const TYPES = [
  { key: 'wheel', label: 'Hjul' },
  { key: 'tire', label: 'Däck' },
  { key: 'rim', label: 'Fälg' },
];

/* Skick toggle — Alla / Nya / Begagnade, same pattern as the season
 * toggle. "Nya" maps to the config's `new` enum option; "Begagnade"
 * writes every other grade at once, so each grade shows up as its own
 * removable result pill and can be peeled off individually. */
const CONDITION_TOGGLE = [
  { key: '', label: 'Alla' },
  { key: 'new', label: 'Nya' },
  { key: 'used', label: 'Begagnade' },
];

/* Season options map to the `tiretype` enum used across the app
 * (CategoryNav / CategoryGrid). "Vinter" groups friction + studded. */
const WINTER_TYPES = ['winterFriction', 'winterDubbed'];
const SEASONS = [
  { key: '', label: 'Alla' },
  { key: 'summer', label: 'Sommar' },
  { key: WINTER_TYPES.join(','), label: 'Vinter' },
];
/* Under Vinter: friction, studded or both (BR-2 "Däcktyp"). */
const WINTER_TYRE_TYPES = [
  { key: 'winterFriction', label: 'Friktion' },
  { key: 'winterDubbed', label: 'Dubb' },
];

/* Every dimension param this widget owns. Cleared before each apply so
 * switching type or editing never leaves a contradictory mix. Exported
 * for the car bar's Hjul / Däck / Fälg, which switches the product with the
 * same clearing semantics when there is no car (P25009-491). */
export const DIM_KEYS = [
  'pub_tireWidthFront',
  'pub_tireWidthRear',
  'pub_profileFront',
  'pub_profileRear',
  'pub_rimWidthFront',
  'pub_rimWidthRear',
  'pub_diameter',
  'pub_numberOfBolts',
  'pub_boltCircle',
  'pub_etFront',
  'pub_hubHole',
];

/* Absolute bounds for the range sliders (ET offset + hub bore). Only
 * written to the URL once narrowed from the full range. */
const ET_MIN = -30;
const ET_MAX = 70;
const HUB_MIN = 50;
const HUB_MAX = 75;

/* Pris slider bounds come from the hosted search config. The template
 * ships a 0–1000 placeholder max, so anything that small is widened to
 * a ceiling that fits wheel/tyre prices on the marketplace. */
const PRICE_FALLBACK_MAX = 50000;

/* Suggestion lists shown as the user types (native datalist). */
const TIRE_WIDTHS = [165, 175, 185, 195, 205, 215, 225, 235, 245, 255, 265, 275, 285, 295, 305];
const RIM_WIDTHS = [6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10, 10.5, 11, 11.5, 12];
const PROFILES = [25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80];
const DIAMETERS = [14, 15, 16, 17, 18, 19, 20, 21, 22, 23];
const BOLT_COUNTS = [3, 4, 5, 6, 8];
const BOLT_CIRCLES = [98, 100, 108, 110, 112, 114.3, 115, 118, 120, 127, 130, 139.7];

/* Strip everything but digits and a decimal point — rim widths like
 * 8.5 are valid, but stray letters/spaces from paste are not. */
const num = v => (v || '').toString().replace(/[^\d.]/g, '');

/* A car search keeps to the car's own diameters ("18,19,20,21,22",
 * P25009-492). That list is not a size the buyer typed: the box stays empty,
 * and the list stays in the URL until the buyer types a diameter — num()
 * would otherwise turn it into "1819202122". */
const isDiameterList = v => String(v || '').includes(',');

/* A set chosen in the car bar ("235/55R19 fram · 255/50R19 bak") is one
 * `pub_combination` value, not the size params — read its tyre sizes so the
 * Däckdimension group shows the chosen chip (P25009-491 BR-5). */
const combinationTires = p =>
  String(p.get('pub_combination') || '')
    .replace(/^has_(all|any):/, '')
    .split(',')
    .map(size => /^(\d+)\/(\d+)\s*Z?R(\d+)/i.exec(size.trim()))
    .filter(Boolean);

const fieldsFromSearch = search => {
  const p = new URLSearchParams(search);
  const hasSizeParams = !!(p.get('pub_tireWidthFront') || p.get('pub_rimWidthFront'));
  const [setFront, setRear] = hasSizeParams ? [] : combinationTires(p);
  return {
    diameter: setFront
      ? setFront[3]
      : isDiameterList(p.get('pub_diameter'))
        ? ''
        : p.get('pub_diameter') || '',
    widthFront: setFront ? setFront[1] : p.get('pub_tireWidthFront') || p.get('pub_rimWidthFront') || '',
    profileFront: setFront ? setFront[2] : p.get('pub_profileFront') || '',
    widthRear: setRear ? setRear[1] : p.get('pub_tireWidthRear') || p.get('pub_rimWidthRear') || '',
    profileRear: setRear ? setRear[2] : p.get('pub_profileRear') || '',
    numberOfBolts: p.get('pub_numberOfBolts') || '',
    /* The param carries every spelling of one circle ("139.7,139_7,1397");
     * the box shows the number once. */
    boltCircle:
      (p.get('pub_boltCircle') || '')
        .split(',')
        .map(boltCircleText)
        .find(Boolean) || '',
    et: p.get('pub_etFront') || '',
    hub: p.get('pub_hubHole') || '',
    price: p.get('price') || '',
  };
};

const hasRear = search => {
  const p = new URLSearchParams(search);
  return (
    !!(p.get('pub_tireWidthRear') || p.get('pub_rimWidthRear')) ||
    (!p.get('pub_tireWidthFront') && combinationTires(p).length > 1)
  );
};

/* Parse a "min,max" range string into a [lo, hi] handles array, falling
 * back to the full bounds when unset. Emit '' when the user widens back
 * to the bounds so the query param is dropped rather than matching all. */
const toHandles = (value, min, max) => (value ? value.split(',').map(Number) : [min, max]);
const fromHandles = (handles, min, max) => {
  const [a, b] = handles;
  return a === min && b === max ? '' : `${a},${b}`;
};

/* Navhål (hubHole) is a discrete enum (option keys like "66_6"), so its
 * slider snaps to the field's actual values rather than a continuous
 * range — evenly spaced by index, the way Gummigrossen shows them. */
const buildEnumValues = options =>
  (options || [])
    .map(o => ({
      key: String(o.option),
      label: o.label || String(o.option).replace(/_/g, '.'),
      num: parseFloat(String(o.option).replace(/_/g, '.').replace(',', '.')),
    }))
    .filter(o => !Number.isNaN(o.num))
    .sort((a, b) => a.num - b.num);

/* Strip a "has_any:"/"has_all:" prefix from a multi-enum param value. */
const stripSearchMode = v => (v && v.includes(':') ? v.split(':')[1] : v || '');

const DimensionQuickSearch = ({
  activeType = 'tire',
  onSelectType,
  hubHoleConfig,
  conditionConfig,
  rimTypeConfig,
  priceConfig,
  className,
  /* P25009-432 BR-5 — inside the mobile filter sheet every section becomes a
   * collapsible group: a group with a set value is always open and prints its
   * value in the header; the everyday groups start open and the expert groups
   * (bultcirkel, ET, navhål) start closed. Desktop keeps the always-expanded
   * card, so this defaults off. */
  collapsible = false,
  /* With a car the product is chosen in the car's own tabs (CarInfoSidebar /
   * VehicleCard), so the filter does not offer it twice (P25009-486 BR-2). */
  hideTypeToggle = false,
}) => {
  const history = useHistory();
  const location = useLocation();

  const isRim = activeType === 'rim';
  const activeSeason = new URLSearchParams(location.search).get('pub_tiretype') || '';
  /* Friction, studded or both — any of them is "Vinter". */
  const activeWinterTypes = activeSeason
    .split(',')
    .filter(key => WINTER_TYPES.includes(key));
  const isWinter =
    activeWinterTypes.length > 0 && activeWinterTypes.length === activeSeason.split(',').length;

  /* Skick — the config's enum options split into "new" vs everything
   * else. The toggle is active on "Begagnade" as long as the selection
   * only holds used grades, so peeling off a grade pill keeps it lit. */
  const conditionOptions = (conditionConfig?.enumOptions || []).map(o => ({
    key: String(o.option),
    label: o.label || String(o.option),
  }));
  const usedConditionKeys = conditionOptions.filter(o => o.key !== 'new').map(o => o.key);
  const selectedConditions = stripSearchMode(
    new URLSearchParams(location.search).get('pub_condition') || ''
  )
    .split(',')
    .filter(Boolean);
  const activeCondition =
    selectedConditions.length === 0
      ? ''
      : selectedConditions.every(k => k === 'new')
        ? 'new'
        : selectedConditions.every(k => usedConditionKeys.includes(k))
          ? 'used'
          : '';

  /* Fälgtyp — data-driven from the hosted field config, rendered as a
   * toggle for rims + complete wheels. */
  const rimTypeOptions = (rimTypeConfig?.enumOptions || []).map(o => ({
    key: String(o.option),
    label: o.label || String(o.option),
  }));
  const activeRimType = stripSearchMode(
    new URLSearchParams(location.search).get('pub_rimType') || ''
  );

  const priceMin = priceConfig?.min ?? 0;
  const priceMax = priceConfig?.max > 1000 ? priceConfig.max : PRICE_FALLBACK_MAX;
  const priceStep = Math.max(priceConfig?.step || 0, 100);
  const formatKr = v => `${Number(v).toLocaleString('sv-SE')} kr`;

  const [fields, setFields] = useState(() => fieldsFromSearch(location.search));
  const [staggered, setStaggered] = useState(() => hasRear(location.search));

  /* Which collapsible groups are open. P25009-486 BR-2: every group folds,
   * and a folded group prints its choice in the header ("Vinter", "Nya",
   * "5×108"), so nothing that affects the results is out of sight. The
   * season and the size start open. */
  const [openGroups, setOpenGroups] = useState({
    season: true,
    tyreType: true,
    dimension: true,
    /* One row of chips, and what local buyers look for first (P25009-425). */
    distance: true,
  });
  const distance = useDistanceFilter();
  const toggleGroup = id => setOpenGroups(g => ({ ...g, [id]: !g[id] }));

  /* The search string of our own last push — lets the URL→fields sync
   * effect ignore changes we caused (so typing doesn't fight itself)
   * while still picking up external changes (REG lookup, dim chips,
   * pill removal, "Ny sökning"). */
  const selfPushRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (location.search === selfPushRef.current) return;
    setFields(fieldsFromSearch(location.search));
    setStaggered(hasRear(location.search));
  }, [location.search]);

  useEffect(() => () => debounceRef.current && clearTimeout(debounceRef.current), []);

  const buildSearch = (nextFields, nextStaggered, type) => {
    const p = new URLSearchParams(history.location.search);
    const carDiameters = isDiameterList(p.get('pub_diameter')) ? p.get('pub_diameter') : null;
    DIM_KEYS.forEach(k => p.delete(k));
    /* A size typed here replaces a set chosen in the car bar. */
    p.delete('pub_combination');
    p.delete('axle');
    p.delete('page');
    const set = (k, v) => {
      const n = num(v);
      if (n) p.set(k, n);
    };
    p.set('pub_adType', type);
    set('pub_diameter', nextFields.diameter);
    if (!num(nextFields.diameter) && carDiameters) p.set('pub_diameter', carDiameters);
    if (type === 'rim') {
      set('pub_rimWidthFront', nextFields.widthFront);
      if (nextStaggered) set('pub_rimWidthRear', nextFields.widthRear);
    } else {
      set('pub_tireWidthFront', nextFields.widthFront);
      set('pub_profileFront', nextFields.profileFront);
      if (nextStaggered) {
        set('pub_tireWidthRear', nextFields.widthRear);
        set('pub_profileRear', nextFields.profileRear);
      }
    }
    /* Bolt pattern + ET + hub bore apply to rims + complete wheels, not
     * bare tires. ET/hub are range filters ("min,max"); set directly. */
    if (type !== 'tire') {
      set('pub_numberOfBolts', nextFields.numberOfBolts);
      /* Every stored spelling, as one OR: Console's key (`114_3`, and `1397`
       * for 139.7) and the plain number older imports wrote. Not through
       * num(), which strips a comma — "114,3" became "1143" (P25009-488). */
      const circles = boltCircleSpellings(nextFields.boltCircle);
      if (circles.length > 0) p.set('pub_boltCircle', circles.join(','));
      if (nextFields.et) p.set('pub_etFront', nextFields.et);
      if (nextFields.hub) p.set('pub_hubHole', nextFields.hub);
    }
    /* Pris is category-agnostic — it rides along with every rebuild. */
    p.delete('price');
    if (nextFields.price) p.set('price', nextFields.price);
    return `?${p.toString()}`;
  };

  /* Live filter — same behaviour as the old dropdown filters, just
   * debounced so we don't fire a search on every keystroke. */
  const applyDebounced = (nextFields, nextStaggered) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const search = buildSearch(nextFields, nextStaggered, activeType);
      selfPushRef.current = search;
      history.push({ search });
    }, 350);
  };

  const set = (k, v) => {
    const next = { ...fields, [k]: v };
    setFields(next);
    applyDebounced(next, staggered);
  };

  /* Sliders update the same way as text fields, but onChange already
   * gives us the final "min,max" string (or '') so we reuse set(). */
  const setRange = (k, rangeStr) => set(k, rangeStr);

  /* Navhål — discrete slider over the field's real enum values. Handles
   * are indices into hubValues (even spacing); the param is the standard
   * enum value list (with a has_any:/has_all: prefix for multi-enum). */
  const hubValues = buildEnumValues(hubHoleConfig?.enumOptions);
  const hubIsEnum = hubValues.length > 1;
  const hubSearchMode = hubHoleConfig?.filterConfig?.searchMode;
  const hubIsMultiEnum = hubHoleConfig?.schemaType === 'multi-enum';
  const hubSelKeys = fields.hub ? stripSearchMode(fields.hub).split(',').filter(Boolean) : [];
  const hubIdxs = hubSelKeys.map(k => hubValues.findIndex(v => v.key === k)).filter(i => i >= 0);
  const hubHandles = hubIdxs.length
    ? [Math.min(...hubIdxs), Math.max(...hubIdxs)]
    : [0, Math.max(0, hubValues.length - 1)];
  const encodeHub = ([lo, hi]) => {
    if (lo === 0 && hi === hubValues.length - 1) return '';
    const keys = hubValues.slice(lo, hi + 1).map(v => v.key);
    const mode = hubIsMultiEnum && hubSearchMode ? `${hubSearchMode}:` : '';
    return `${mode}${keys.join(',')}`;
  };

  const toggleStaggered = checked => {
    const next = checked ? fields : { ...fields, widthRear: '', profileRear: '' };
    setStaggered(checked);
    setFields(next);
    applyDebounced(next, checked);
  };

  /* Switching type resets inputs + drops stale dimension params, then
   * tells the parent so the filter list below adapts. Season is
   * irrelevant for rims, so drop it there. */
  const selectType = type => {
    if (type === activeType) return;
    if (onSelectType) onSelectType(type);
    const cleared = {
      diameter: '',
      widthFront: '',
      profileFront: '',
      widthRear: '',
      profileRear: '',
      numberOfBolts: '',
      boltCircle: '',
      et: '',
      hub: '',
    };
    setFields(cleared);
    setStaggered(false);
    const p = new URLSearchParams(history.location.search);
    DIM_KEYS.forEach(k => p.delete(k));
    if (type === 'rim') p.delete('pub_tiretype');
    if (type === 'tire') p.delete('pub_rimType');
    p.set('pub_adType', type);
    const search = `?${p.toString()}`;
    selfPushRef.current = search;
    history.push({ search });
  };

  /* Friktion / Dubb under Vinter: toggles one winter type; with neither
   * left it falls back to both (= plain "Vinter"). */
  const toggleWinterType = key => {
    const current = isWinter ? activeWinterTypes : WINTER_TYPES;
    const next = current.includes(key)
      ? current.filter(type => type !== key)
      : [...current, key];
    selectSeason((next.length ? next : WINTER_TYPES).join(','));
  };

  const selectSeason = key => {
    const p = new URLSearchParams(history.location.search);
    p.delete('pub_tiretype');
    p.set('pub_adType', activeType);
    if (key) p.set('pub_tiretype', key);
    const search = `?${p.toString()}`;
    selfPushRef.current = search;
    history.push({ search });
  };

  /* Skick — same direct-push pattern as the season toggle. "Begagnade"
   * writes every non-new grade so each renders as its own removable
   * result pill above the listings. */
  const selectCondition = key => {
    const p = new URLSearchParams(history.location.search);
    p.delete('pub_condition');
    p.set('pub_adType', activeType);
    if (key === 'new') p.set('pub_condition', 'new');
    else if (key === 'used' && usedConditionKeys.length)
      p.set('pub_condition', usedConditionKeys.join(','));
    const search = `?${p.toString()}`;
    selfPushRef.current = search;
    history.push({ search });
  };

  const selectRimType = key => {
    const p = new URLSearchParams(history.location.search);
    p.delete('pub_rimType');
    p.set('pub_adType', activeType);
    if (key) p.set('pub_rimType', key);
    const search = `?${p.toString()}`;
    selfPushRef.current = search;
    history.push({ search });
  };

  const dimLabel = isRim ? 'Fälgmått' : 'Däckdimension';
  const widthList = isRim ? 'dqsRimWidths' : 'dqsTireWidths';

  /* Value summaries printed in a group's header when it holds a value
   * (sheet mode, BR-5) — "235/55R19", "5×108", "ET -10 till 45"… */
  /* "Alla" is the no-filter default, not a value — it must not lock the
   * group open or print in the header. */
  const seasonSummary = isWinter
    ? 'Vinter'
    : activeSeason
      ? (SEASONS.find(s => s.key === activeSeason) || {}).label || ''
      : '';
  const tyreTypeSummary =
    isWinter && activeWinterTypes.length < WINTER_TYPES.length
      ? WINTER_TYRE_TYPES.filter(t => activeWinterTypes.includes(t.key))
          .map(t => t.label)
          .join(', ')
      : '';
  const conditionSummary =
    selectedConditions.length === 0
      ? ''
      : activeCondition === 'new'
        ? 'Nya'
        : activeCondition === 'used'
          ? 'Begagnade'
          : conditionOptions
              .filter(o => selectedConditions.includes(o.key))
              .map(o => o.label)
              .join(', ');
  const fmtDim = (w, p) => (isRim ? (w ? `${w}"` : '') : [w, p].filter(Boolean).join('/'));
  const frontDim = fmtDim(fields.widthFront, fields.profileFront);
  const rearDim = staggered ? fmtDim(fields.widthRear, fields.profileRear) : '';
  const diaDim = fields.diameter ? `R${fields.diameter}` : '';
  const dimensionSummary = [
    frontDim || diaDim ? `${frontDim}${diaDim}` : '',
    rearDim ? `${rearDim}${diaDim} bak` : '',
  ]
    .filter(Boolean)
    .join(' + ');
  const boltSummary = [fields.numberOfBolts, fields.boltCircle].filter(Boolean).join('×');
  const etSummary = fields.et
    ? `${toHandles(fields.et, ET_MIN, ET_MAX)[0]} till ${toHandles(fields.et, ET_MIN, ET_MAX)[1]}`
    : '';
  const hubSummary = !fields.hub
    ? ''
    : hubIsEnum
      ? `${hubValues[hubHandles[0]].label}–${hubValues[hubHandles[1]].label} mm`
      : `${toHandles(fields.hub, HUB_MIN, HUB_MAX)[0]}–${toHandles(fields.hub, HUB_MIN, HUB_MAX)[1]} mm`;
  const rimTypeSummary = (rimTypeOptions.find(o => o.key === activeRimType) || {}).label || '';
  const priceSummary = fields.price
    ? `${formatKr(toHandles(fields.price, priceMin, priceMax)[0])} – ${formatKr(
        toHandles(fields.price, priceMin, priceMax)[1]
      )}`
    : '';

  /* Group wrapper (plain function, not a component — a per-render component
   * type would remount its subtree and drop input focus while typing).
   * P25009-486 BR-2: any group folds; its header always says what is chosen
   * ("Alla" when nothing is), so a folded group still shows its selection. */
  const renderGroup = (id, title, value, content) => {
    const hasValue = !!value;
    const isOpen = !!openGroups[id];
    return (
      <div className={css.group}>
        <button
          type="button"
          className={css.groupHeader}
          aria-expanded={isOpen}
          onClick={() => toggleGroup(id)}
        >
          <span className={css.groupTitle}>{title}</span>
          <span className={css.groupHeaderRight}>
            <span className={hasValue ? css.groupValue : css.groupValueNone}>
              {hasValue ? value : 'Alla'}
            </span>
            <svg
              className={classNames(css.groupChevron, { [css.groupChevronOpen]: isOpen })}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </span>
        </button>
        {isOpen ? <div className={css.groupBody}>{content}</div> : null}
      </div>
    );
  };

  /* Section contents, shared verbatim between the desktop card (wrapped in
   * .fields + .dimHeader exactly as before) and the sheet's groups. */
  const seasonContent = (
    <div className={css.seasonRow} role="tablist" aria-label="Säsong">
      {SEASONS.map(s => {
        const selected = s.label === 'Vinter' ? isWinter : activeSeason === s.key;
        return (
          <button
            key={s.key || 'all'}
            type="button"
            role="tab"
            aria-selected={selected}
            className={classNames(css.seasonBtn, { [css.seasonBtnActive]: selected })}
            onClick={() => selectSeason(s.key)}
          >
            <span>{s.label}</span>
          </button>
        );
      })}
    </div>
  );

  const tyreTypeContent = (
    <div className={css.seasonRow} aria-label="Däcktyp">
      {WINTER_TYRE_TYPES.map(t => {
        const selected = activeWinterTypes.includes(t.key);
        return (
          <button
            key={t.key}
            type="button"
            aria-pressed={selected}
            className={classNames(css.seasonBtn, { [css.seasonBtnActive]: selected })}
            onClick={() => toggleWinterType(t.key)}
          >
            <span>{t.label}</span>
          </button>
        );
      })}
    </div>
  );

  /* "25 km · 50 km · 100 km · Hela Sverige" (P25009-425 BR-2), measured from
   * the buyer's approximate area — asked for only when a chip needs it. */
  const distanceContent = (
    <>
      <div className={css.seasonRow} role="radiogroup" aria-label="Avstånd">
        {DISTANCE_CHIPS.map(chip => {
          const selected = distance.radiusKm === chip.km;
          return (
            <button
              key={chip.km || 'all'}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={distance.busy}
              className={classNames(css.seasonBtn, { [css.seasonBtnActive]: selected })}
              onClick={() => (selected ? null : distance.select(chip.km))}
            >
              <span>{chip.label}</span>
            </button>
          );
        })}
      </div>
      <p className={css.distanceNote} role={distance.notice ? 'status' : undefined}>
        {distance.notice ||
          (distance.busy
            ? 'Hämtar din plats …'
            : 'Räknat från ditt ungefärliga område, aldrig en adress.')}
      </p>
    </>
  );

  const conditionContent = (
    <div className={css.seasonRow} role="tablist" aria-label="Skick">
      {CONDITION_TOGGLE.map(c => (
        <button
          key={c.key || 'all'}
          type="button"
          role="tab"
          aria-selected={activeCondition === c.key}
          className={classNames(css.seasonBtn, {
            [css.seasonBtnActive]: activeCondition === c.key,
          })}
          onClick={() => selectCondition(c.key)}
        >
          <span>{c.label}</span>
        </button>
      ))}
    </div>
  );

  const dimensionContent = (
    <>
      <div className={css.dimRow}>
        <input
          className={css.inputSm}
          inputMode="decimal"
          list={widthList}
          placeholder={isRim ? '8.5' : '235'}
          aria-label={isRim ? 'Fälgbredd' : 'Däckbredd fram'}
          value={fields.widthFront}
          onChange={e => set('widthFront', e.target.value)}
        />
        {!isRim && (
          <>
            <span className={css.sep}>/</span>
            <input
              className={css.inputSm}
              inputMode="numeric"
              list="dqsProfiles"
              placeholder="55"
              aria-label="Profil fram"
              value={fields.profileFront}
              onChange={e => set('profileFront', e.target.value)}
            />
          </>
        )}
        <span className={css.sep}>R</span>
        <input
          className={css.inputSm}
          inputMode="numeric"
          list="dqsDiameters"
          placeholder="19"
          aria-label="Diameter"
          value={fields.diameter}
          onChange={e => set('diameter', e.target.value)}
        />
      </div>

      <label className={css.checkboxRow}>
        <input
          type="checkbox"
          checked={staggered}
          onChange={e => toggleStaggered(e.target.checked)}
        />
        Lägg till bak-mått (staggered)
      </label>

      {staggered && (
        <>
          {!isRim && <div className={css.rowTag}>Bak</div>}
          <div className={css.dimRow}>
          <input
            className={css.inputSm}
            inputMode="decimal"
            list={widthList}
            placeholder={isRim ? '10' : '255'}
            aria-label={isRim ? 'Fälgbredd bak' : 'Däckbredd bak'}
            value={fields.widthRear}
            onChange={e => set('widthRear', e.target.value)}
          />
          {!isRim && (
            <>
              <span className={css.sep}>/</span>
              <input
                className={css.inputSm}
                inputMode="numeric"
                list="dqsProfiles"
                placeholder="50"
                aria-label="Profil bak"
                value={fields.profileRear}
                onChange={e => set('profileRear', e.target.value)}
              />
            </>
          )}
          <span className={css.sep}>R</span>
          <input
            className={css.inputSm}
            inputMode="numeric"
            placeholder="19"
            aria-label="Diameter bak"
            value={fields.diameter}
            onChange={e => set('diameter', e.target.value)}
            disabled
            title="Delad diameter fram/bak"
          />
          </div>
        </>
      )}
    </>
  );

  const boltContent = (
    <div className={css.dimRow}>
      <input
        className={css.inputSm}
        inputMode="numeric"
        list="dqsBoltCounts"
        placeholder="5"
        aria-label="Antal bultar"
        value={fields.numberOfBolts}
        onChange={e => set('numberOfBolts', e.target.value)}
      />
      <span className={css.sep}>/</span>
      <input
        className={css.inputSm}
        inputMode="decimal"
        list="dqsBoltCircles"
        placeholder="108"
        aria-label="Bultcirkel"
        value={fields.boltCircle}
        onChange={e => set('boltCircle', e.target.value)}
      />
    </div>
  );

  const etContent = (
    <>
      <RangeSlider
        min={ET_MIN}
        max={ET_MAX}
        step={1}
        handles={toHandles(fields.et, ET_MIN, ET_MAX)}
        onChange={handles => setRange('et', fromHandles(handles, ET_MIN, ET_MAX))}
      />
      <div className={css.sliderVals}>
        <span>{toHandles(fields.et, ET_MIN, ET_MAX)[0]}</span>
        <span>{toHandles(fields.et, ET_MIN, ET_MAX)[1]}</span>
      </div>
    </>
  );

  const hubContent = hubIsEnum ? (
    <>
      <RangeSlider
        min={0}
        max={hubValues.length - 1}
        step={1}
        handles={hubHandles}
        onChange={handles => setRange('hub', encodeHub(handles))}
      />
      <div className={css.sliderVals}>
        <span>{hubValues[hubHandles[0]].label} mm</span>
        <span>{hubValues[hubHandles[1]].label} mm</span>
      </div>
    </>
  ) : (
    <>
      <RangeSlider
        min={HUB_MIN}
        max={HUB_MAX}
        step={1}
        handles={toHandles(fields.hub, HUB_MIN, HUB_MAX)}
        onChange={handles => setRange('hub', fromHandles(handles, HUB_MIN, HUB_MAX))}
      />
      <div className={css.sliderVals}>
        <span>{toHandles(fields.hub, HUB_MIN, HUB_MAX)[0]} mm</span>
        <span>{toHandles(fields.hub, HUB_MIN, HUB_MAX)[1]} mm</span>
      </div>
    </>
  );

  const rimTypeContent = (
    <div className={css.seasonRow} role="tablist" aria-label="Fälgtyp">
      <button
        type="button"
        role="tab"
        aria-selected={activeRimType === ''}
        className={classNames(css.seasonBtn, {
          [css.seasonBtnActive]: activeRimType === '',
        })}
        onClick={() => selectRimType('')}
      >
        <span>Alla</span>
      </button>
      {rimTypeOptions.map(o => (
        <button
          key={o.key}
          type="button"
          role="tab"
          aria-selected={activeRimType === o.key}
          className={classNames(css.seasonBtn, {
            [css.seasonBtnActive]: activeRimType === o.key,
          })}
          onClick={() => selectRimType(o.key)}
        >
          <span>{o.label}</span>
        </button>
      ))}
    </div>
  );

  const priceContent = (
    <>
      <RangeSlider
        min={priceMin}
        max={priceMax}
        step={priceStep}
        handles={toHandles(fields.price, priceMin, priceMax)}
        onChange={handles => setRange('price', fromHandles(handles, priceMin, priceMax))}
      />
      <div className={css.sliderVals}>
        <span>{formatKr(toHandles(fields.price, priceMin, priceMax)[0])}</span>
        <span>{formatKr(toHandles(fields.price, priceMin, priceMax)[1])}</span>
      </div>
    </>
  );

  return (
    <div className={classNames(css.root, { [css.rootCollapsible]: collapsible }, className)}>
      {hideTypeToggle ? null : (
        <div className={css.toggle} role="tablist" aria-label="Produkttyp">
          {TYPES.map(t => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={activeType === t.key}
              className={classNames(css.toggleBtn, {
                [css.toggleBtnActive]: activeType === t.key,
              })}
              onClick={() => selectType(t.key)}
            >
              <span>{t.label}</span>
            </button>
          ))}
        </div>
      )}

      {collapsible ? (
        /* Mobile filter sheet — every section is a collapsible group. The
         * everyday groups (säsong, skick, dimension, bil, pris) start open;
         * the expert groups (bultcirkel, ET, navhål, fälgtyp) start closed.
         * A group with a value is always open with the value in its header. */
        <>
          {!isRim && renderGroup('season', 'Säsong', seasonSummary, seasonContent)}
          {!isRim && isWinter
            ? renderGroup('tyreType', 'Däcktyp', tyreTypeSummary, tyreTypeContent)
            : null}
          {conditionOptions.length > 0 &&
            renderGroup('condition', 'Skick', conditionSummary, conditionContent)}
          {renderGroup('dimension', dimLabel, dimensionSummary, dimensionContent)}
          {activeType !== 'tire' && (
            <>
              {renderGroup('bolt', 'Bultcirkel', boltSummary, boltContent)}
              {renderGroup('et', 'ET (inpressningsdjup)', etSummary, etContent)}
              {renderGroup('hub', 'Navhål (CB)', hubSummary, hubContent)}
            </>
          )}
          {activeType !== 'tire' &&
            rimTypeOptions.length > 0 &&
            renderGroup('rimType', 'Fälgtyp', rimTypeSummary, rimTypeContent)}
          {/* No "Bilmärke & modell" group: the car is chosen in the car bar's
            * "Byt bil" panel, by plate or by model (P25009-491 BR-5). */}
          {renderGroup('price', 'Pris', priceSummary, priceContent)}
          {renderGroup('distance', 'Avstånd', radiusLabel(distance.radiusKm), distanceContent)}
        </>
      ) : (
        /* Desktop card — always expanded, exactly the pre-432 layout. */
        <>
          {!isRim && seasonContent}

          {conditionOptions.length > 0 && (
            <div className={css.fields}>
              <div className={css.dimHeader}>Skick</div>
              {conditionContent}
            </div>
          )}

          <div className={css.fields}>
            <div className={css.dimHeader}>{dimLabel}</div>
            {dimensionContent}
          </div>

          {activeType !== 'tire' && (
            <div className={css.fields}>
              <div className={css.dimHeader}>Bultcirkel</div>
              {boltContent}

              <div className={css.dimHeader}>ET (inpressningsdjup)</div>
              {etContent}

              <div className={css.dimHeader}>Navhål (CB)</div>
              {hubContent}
            </div>
          )}

          {activeType !== 'tire' && rimTypeOptions.length > 0 && (
            <div className={css.fields}>
              <div className={css.dimHeader}>Fälgtyp</div>
              {rimTypeContent}
            </div>
          )}

          <div className={css.fields}>
            <div className={css.dimHeader}>Pris</div>
            {priceContent}
          </div>
        </>
      )}

      {/* "Bevaka" sits in the results toolbar since P25009-491 (BR-5). */}

      <datalist id="dqsBoltCounts">
        {BOLT_COUNTS.map(b => (
          <option key={b} value={b} />
        ))}
      </datalist>
      <datalist id="dqsBoltCircles">
        {BOLT_CIRCLES.map(b => (
          <option key={b} value={b} />
        ))}
      </datalist>
      <datalist id="dqsTireWidths">
        {TIRE_WIDTHS.map(w => (
          <option key={w} value={w} />
        ))}
      </datalist>
      <datalist id="dqsRimWidths">
        {RIM_WIDTHS.map(w => (
          <option key={w} value={w} />
        ))}
      </datalist>
      <datalist id="dqsProfiles">
        {PROFILES.map(p => (
          <option key={p} value={p} />
        ))}
      </datalist>
      <datalist id="dqsDiameters">
        {DIAMETERS.map(d => (
          <option key={d} value={d} />
        ))}
      </datalist>
    </div>
  );
};

export default DimensionQuickSearch;
